import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useConfig } from "../../context/ConfigContext";
import { termsApi } from "../../api/terms";
import { campersApi } from "../../api/campers";
import { activitiesApi } from "../../api/activities";
import { enrollmentsApi } from "../../api/enrollments";
import { cabinGroupsApi } from "../../api/cabinGroups";
import { getStoredToken } from "../../context/AuthContext";
import api from "../../api/axios";

type CheckStatus = "idle" | "running" | "pass" | "fail";

interface CheckResult {
  label: string;
  status: CheckStatus;
  detail?: string;
}

const INITIAL_CHECKS: CheckResult[] = [
  { label: "Token present", status: "idle" },
  { label: "Auth — GET /auth/me", status: "idle" },
  { label: "Terms — GET /terms", status: "idle" },
  { label: "Campers — GET /campers", status: "idle" },
  { label: "Camper detail — GET /campers/:id", status: "idle" },
  { label: "Enrollments — GET /enrollments", status: "idle" },
  { label: "Activities — GET /activities", status: "idle" },
  { label: "Cabin groups — GET /cabin-groups", status: "idle" },
  { label: "Dashboard — GET /dashboard/stats", status: "idle" },
];

function StatusBadge({ status, detail }: { status: CheckStatus; detail?: string }) {
  if (status === "idle") return <span className="text-xs text-stone-400">—</span>;
  if (status === "running") return <span className="text-xs text-amber-600 animate-pulse">running…</span>;
  if (status === "pass") return <span className="text-xs font-semibold text-green-700">✓ PASS</span>;
  return (
    <span className="text-xs font-semibold text-red-700">
      ✗ FAIL{detail ? `: ${detail}` : ""}
    </span>
  );
}

export default function DebugDiagnostics() {
  const { user } = useAuth();
  const { config } = useConfig();
  const [checks, setChecks] = useState<CheckResult[]>(INITIAL_CHECKS);
  const [running, setRunning] = useState(false);

  const isAdminOrDirector = user?.role === "admin" || user?.role === "director";
  const debugEnabled = config["debug.mode"] === "true";

  if (!isAdminOrDirector || !debugEnabled) return null;

  function setCheck(index: number, update: Partial<CheckResult>) {
    setChecks((prev) => prev.map((c, i) => (i === index ? { ...c, ...update } : c)));
  }

  async function runDiagnostics() {
    setRunning(true);
    setChecks(INITIAL_CHECKS.map((c) => ({ ...c, status: "running" as CheckStatus })));

    // 0: Token present (synchronous)
    const token = getStoredToken();
    setCheck(0, token ? { status: "pass" } : { status: "fail", detail: "no token in localStorage" });

    // Run remaining checks in parallel
    const results = await Promise.allSettled([
      api.get("/auth/me"),
      termsApi.list(),
      campersApi.list({ page: 1, pageSize: 1 }),
      activitiesApi.list(),
      enrollmentsApi.list(),
      cabinGroupsApi.list(),
      api.get("/dashboard/stats"),
    ]);

    const labels = [1, 2, 3, 5, 6, 7, 8]; // indices in checks array
    results.forEach((result, i) => {
      const idx = labels[i];
      if (result.status === "fulfilled") {
        setCheck(idx, { status: "pass" });
      } else {
        const err = result.reason as Error;
        setCheck(idx, { status: "fail", detail: err.message ?? "unknown error" });
      }
    });

    // Check 4: camper detail — use first camper from check 3 result
    const campersResult = results[2];
    if (campersResult.status === "fulfilled") {
      const data = campersResult.value as Awaited<ReturnType<typeof campersApi.list>>;
      const firstId = data.data?.[0]?.id;
      if (firstId) {
        try {
          await campersApi.get(firstId);
          setCheck(4, { status: "pass" });
        } catch (err) {
          setCheck(4, { status: "fail", detail: (err as Error).message });
        }
      } else {
        setCheck(4, { status: "pass", detail: "no campers to fetch" });
      }
    } else {
      setCheck(4, { status: "fail", detail: "skipped (camper list failed)" });
    }

    setRunning(false);
  }

  const allDone = checks.every((c) => c.status !== "idle" && c.status !== "running");
  const passCount = checks.filter((c) => c.status === "pass").length;
  const failCount = checks.filter((c) => c.status === "fail").length;

  return (
    <div>
      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">API Diagnostics</p>
      <div className="bg-white/70 border border-amber-100 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-3">
          <button
            onClick={runDiagnostics}
            disabled={running}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {running ? "Running…" : "Run Diagnostics"}
          </button>
          {allDone && (
            <span className="text-xs text-stone-600">
              {passCount} passed, {failCount} failed
            </span>
          )}
        </div>
        <table className="w-full text-xs">
          <tbody>
            {checks.map((check, i) => (
              <tr key={i} className="border-t border-amber-50 first:border-t-0">
                <td className="py-1 pr-3 text-stone-600 font-mono">{check.label}</td>
                <td className="py-1 text-right">
                  <StatusBadge status={check.status} detail={check.detail} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
